import {
    Box,
    Button,
    ButtonRow,
    Card,
    DatePicker,
    Form,
    FormGroup,
    Select,
    Switch,
    Text,
    TextInput,
} from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { ServiceType } from '@common/enums/';
import type { IEpisodeCategory } from '@common/interfaces/episodeCategory';
import type { IAdminProcessesListSearchState } from '@frontend/components/interfaces/adminProcessesList';
import type { ValidationErrors } from '@frontend/utils/';
import { AdminProcessesFields } from '@common/enums/adminProcessesFields';
import { getObjectKeys } from '@common/utils/getObjectKeys';
import { adminProcessesFilterInputFieldSchema } from '@common/validation/adminProcesses';
import { getSelectedLanguage } from '@frontend/translate';
import {
    emptyValidationErrors,
    getValidationErrorTranslation,
    updateValidationErrorsForMany,
    updateValidationErrorsForOne,
    useFetchItems,
    useStateWithPartialSet,
} from '@frontend/utils/';
import { formatDateIntoString } from '@frontend/utils/formatDateIntoString';
import type { IService } from '../adminProcessesHelperFunctions';
import {
    getCleanAdminProcessesSearchState,
    getEpisodeCategoryFilterValue,
    getEpisodeCategoryOptions,
    getSelectServiceOptions,
    getServiceFilterValue,
} from '../adminProcessesHelperFunctions';
import { AdminProcessesListFilterStatus } from './header/AdminProcessesListFilterStatus';

interface IAdminProcessesListSearchHeaderProps {
    clearSearchState: () => void;
    handleSearch: (partialNewSearchState: Partial<IAdminProcessesListSearchState>) => void;
    searchState: IAdminProcessesListSearchState;
}

export function AdminProcessesListSearchHeader(props: IAdminProcessesListSearchHeaderProps) {
    const language = getSelectedLanguage();
    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const [services, serviceStatus] = useFetchItems<IService>('api/services?selectionList=true');
    const [episodeCategories] = useFetchItems<IEpisodeCategory>('api/episodeCategories?isActive=true');
    const [searchState, setSearchState] = useStateWithPartialSet<IAdminProcessesListSearchState>(
        getCleanAdminProcessesSearchState()
    );

    useEffect(() => {
        setSearchState(props.searchState);
    }, [props.searchState]);

    if (serviceStatus === 'Init' || serviceStatus === 'Loading') {
        return null;
    }

    return (
        <Form
            onSubmit={(e) => {
                e.preventDefault();
                props.handleSearch(searchState);
            }}
            role="search"
            submitOnEnter={true}
        >
            <Card>
                <Card.Body>
                    <Box
                        display="grid"
                        gridTemplateColumns={'repeat(auto-fit, minmax(320px, 1fr))'}
                        marginBottom={4}
                        sx={{ columnGap: 2 }}
                    >
                        <FormGroup
                            hasErrors={errors.has('text')}
                            label={language.common.searchTerm}
                            layout="vertical"
                        >
                            <TextInput
                                error={getValidationErrorTranslation(errors.get('text'))}
                                id="search"
                                onChange={(event) => {
                                    setSearchState({ text: event.target.value });
                                    handleValidateOne('text', event.target.value);
                                }}
                                onReset={() => setSearchState({ text: '' })}
                                placeholder={language.common.searchTermPlaceholder}
                                value={searchState.text}
                            />
                        </FormGroup>
                        <FormGroup
                            label="Zoeken in"
                            layout="vertical"
                        >
                            <Select
                                isClearable={true}
                                onChange={(item) => setSearchState({ field: item?.value || null })}
                                options={Object.values(AdminProcessesFields).map((field) => {
                                    return { label: language.processSearchField[field], value: field };
                                })}
                                placeholder={language.common.select + '...'}
                                value={
                                    searchState.field && {
                                        label: language.processSearchField[searchState.field],
                                        value: searchState.field,
                                    }
                                }
                            />
                        </FormGroup>
                        <FormGroup
                            label="Vakgebied"
                            layout="vertical"
                        >
                            <Select
                                isClearable={true}
                                onChange={(item) => {
                                    setSearchState({
                                        episodeCategoryId: item?.value || null,
                                        serviceId: null,
                                        statusLabel: null,
                                    });
                                }}
                                options={getEpisodeCategoryOptions(episodeCategories)}
                                placeholder={language.common.select + '...'}
                                value={
                                    searchState.episodeCategoryId
                                        ? getEpisodeCategoryFilterValue(
                                              searchState.episodeCategoryId,
                                              episodeCategories
                                          )
                                        : null
                                }
                            />
                        </FormGroup>
                        <FormGroup
                            label="Type"
                            layout="vertical"
                        >
                            <Select
                                isClearable={true}
                                onChange={(item) =>
                                    setSearchState({
                                        serviceId: null,
                                        serviceType: item?.value || null,
                                        statusLabel: null,
                                    })
                                }
                                options={getSelectServiceTypeOptions()}
                                placeholder={language.common.select + '...'}
                                value={
                                    searchState.serviceType && {
                                        label: language.serviceTypeEnum[searchState.serviceType],
                                        value: searchState.serviceType,
                                    }
                                }
                            />
                        </FormGroup>
                        <FormGroup
                            label="Dienst"
                            layout="vertical"
                        >
                            <Select
                                isClearable={true}
                                onChange={(item) => setSearchState({ serviceId: item?.value || null })}
                                options={getSelectServiceOptions(
                                    services,
                                    searchState.serviceType,
                                    searchState.episodeCategoryId
                                )}
                                placeholder={language.common.select + '...'}
                                value={
                                    searchState.serviceId
                                        ? getServiceFilterValue(searchState.serviceId, services)
                                        : null
                                }
                            />
                        </FormGroup>
                        <AdminProcessesListFilterStatus
                            searchState={searchState}
                            services={services}
                            setSearchState={setSearchState}
                        />

                        <FormGroup
                            hasErrors={errors.has('startDate') || errors.has('endDate')}
                            label={language.common.creationDate}
                            layout="vertical"
                        >
                            <Box
                                display="flex"
                                flexWrap={'wrap'}
                                gap={2}
                            >
                                <Box width="min-content">
                                    <DatePicker
                                        error={getValidationErrorTranslation(errors.get('startDate'))}
                                        onChange={(value) => handleChangeDate('startCreationDate', value)}
                                        value={searchState.startCreationDate}
                                    />
                                </Box>
                                <Text
                                    as="span"
                                    mt={2}
                                    size="md"
                                >
                                    {language.common.until}
                                </Text>
                                <Box width="min-content">
                                    <DatePicker
                                        error={getValidationErrorTranslation(errors.get('endDate'))}
                                        onChange={(value) => {
                                            handleChangeDate('endCreationDate', value);
                                        }}
                                        value={searchState.endCreationDate}
                                    />
                                </Box>
                            </Box>
                        </FormGroup>
                        <Box
                            display="flex"
                            justifyContent="space-between"
                        >
                            <FormGroup
                                label={language.processSearchField.automaticRight}
                                layout="vertical"
                                sx={{ mr: 2 }}
                            >
                                <Switch
                                    ariaLabel="checkbox"
                                    checked={searchState.showAutomaticRightsOnly}
                                    className="automation-automatic-rights-only"
                                    id="showAutomaticRightsOnly"
                                    onChange={() =>
                                        setSearchState({
                                            showAutomaticRightsOnly: !searchState.showAutomaticRightsOnly,
                                        })
                                    }
                                />
                            </FormGroup>
                            <FormGroup
                                label={language.common.retrieveTotal}
                                layout="vertical"
                            >
                                <Switch
                                    ariaLabel="checkbox"
                                    checked={searchState.includeTotal}
                                    className="include-total"
                                    id="includeTotal"
                                    onChange={() => setSearchState({ includeTotal: !searchState.includeTotal })}
                                />
                            </FormGroup>
                        </Box>
                    </Box>
                    <ButtonRow
                        isStackedBelowSmall={true}
                        sx={{ alignItems: ['stretch', 'start'], mt: [0, 8] }}
                    >
                        <Button
                            isDisabled={errors.size > 0}
                            type="submit"
                        >
                            {language.common.search}
                        </Button>
                        <Button
                            onClick={handleShowAll}
                            variant="outline"
                        >
                            {language.common.showAll}
                        </Button>
                    </ButtonRow>
                </Card.Body>
            </Card>
        </Form>
    );

    function getSelectServiceTypeOptions() {
        const serviceTypes: { [key: string]: { label: string; value: ServiceType } } = {};
        let filterServices = services;
        if (searchState.episodeCategoryId) {
            filterServices = filterServices.filter(
                (service) => service.episodeCategoryId === searchState.episodeCategoryId
            );
        }
        for (const service of filterServices) {
            if (!serviceTypes[service.serviceType]) {
                serviceTypes[service.serviceType] = {
                    label: language.serviceTypeEnum[service.serviceType],
                    value: service.serviceType,
                };
            }
        }
        if (searchState.serviceType && !serviceTypes[searchState.serviceType]) {
            setSearchState({ serviceType: null });
        }

        return getObjectKeys(serviceTypes).map((key) => serviceTypes[key]);
    }

    function handleValidateDate(filter: keyof IAdminProcessesListSearchState, value: Date | null) {
        const object = {
            endDate: formatDateIntoString(filter === 'endCreationDate' ? value : searchState.endCreationDate),
            startDate: formatDateIntoString(filter === 'startCreationDate' ? value : searchState.startCreationDate),
        };

        const newErrors = updateValidationErrorsForMany(adminProcessesFilterInputFieldSchema, object, errors, [
            'endDate',
            'startDate',
        ]);

        setErrors(newErrors);
    }

    function handleValidateOne(
        name: keyof IAdminProcessesListSearchState,
        value: IAdminProcessesListSearchState[keyof IAdminProcessesListSearchState]
    ) {
        const newErrors = updateValidationErrorsForOne(
            adminProcessesFilterInputFieldSchema,
            { ...searchState, [name]: value },
            errors,
            name
        );
        setErrors(newErrors);
    }

    function handleChangeDate(filter: keyof IAdminProcessesListSearchState, value: Date | null) {
        setSearchState({ [filter]: value });
        handleValidateDate(filter, value);
    }

    function handleShowAll() {
        props.clearSearchState();
        setErrors(emptyValidationErrors);
    }
}
